import { Injectable } from '@angular/core';
import { ApiService, Pagination, PER_PAGE } from './api.service';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, shareReplay, Subject, switchMap, tap, take } from 'rxjs';
import { Paginate } from '@shared/entities/paginate';
import { OdvDocuments } from '@shared/entities/odvDocuments';

const LOG_PREFIX = '[Api][OdvDocuments] ';

export interface OdvDocumentsFilterURL {
  keyword?: string;
  id?: string;
  salesOrderDocumentId?: string;
  documentId?: string;
  userId?: string;
  userName?: string;
  uploaded?: boolean;
  orderBy?: string;
  direction?: 'ASC' | 'DESC';
  locationId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OdvDocumentsService extends ApiService<OdvDocuments> {
  private _refresh$: Subject<Pagination>;
  allOdvDocuments$: Observable<Paginate<OdvDocuments>>;
  private _currentPaginator: Pagination;
  private _currentFilters: OdvDocumentsFilterURL = {};

  set filters(value: OdvDocumentsFilterURL) {
    this._currentFilters = value;
    this.refresh(1, PER_PAGE);
  }

  constructor(private _activatedRoute: ActivatedRoute) {
    const masterUrl = '/odv-documents';
    const detailUrl = '/odv-documents';

    super(OdvDocuments, masterUrl, detailUrl);

    this._currentPaginator = { page: 1, perPage: PER_PAGE };

    this._refresh$ = new BehaviorSubject<Pagination>(this._currentPaginator);
    this.allOdvDocuments$ = this._refresh$.pipe(
      switchMap((pagination) => {
        const { locationId } = this._activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }

        if (this._currentFilters.keyword !== undefined) {
          this.setUrlParameter('keyword', this._currentFilters.keyword);
        }
        if (this._currentFilters.id !== undefined) {
          this.setUrlParameter('id', this._currentFilters.id);
        }
        if (this._currentFilters.salesOrderDocumentId !== undefined) {
          this.setUrlParameter('salesOrderDocumentId', this._currentFilters.salesOrderDocumentId);
        }
        if (this._currentFilters.documentId !== undefined) {
          this.setUrlParameter('documentId', this._currentFilters.documentId);
        }
        if (this._currentFilters.userId !== undefined) {
          this.setUrlParameter('userId', this._currentFilters.userId);
        }
        if (this._currentFilters.userName !== undefined) {
          this.setUrlParameter('userName', this._currentFilters.userName);
        }
        if (this._currentFilters.uploaded !== undefined) {
          this.setUrlParameter('uploaded', this._currentFilters.uploaded.toString());
        }

        const orderBy = this._currentFilters.orderBy || 'id';
        this.setUrlParameter('order_by', orderBy);

        this.setUrlParameter('direction', this._currentFilters.direction || 'DESC');

        return this.paginate(OdvDocuments, pagination.page, pagination.perPage);
      }),
      tap({
        next: (result) => this.logger.debug(LOG_PREFIX + 'all$ odv documents', result),
        error: (err) => this.logger.error(LOG_PREFIX + 'all$ odv documents error', err)
      }),
      shareReplay(1)
    );
  }

  refresh(page: number, perPage: number = PER_PAGE) {
    this._currentPaginator = { page, perPage };
    this._refresh$.next(this._currentPaginator);
    return this.allOdvDocuments$.pipe(take(1));
  }

  refreshFilters() {
    this._currentFilters = {};
    this.refresh(1, PER_PAGE);
  }

  getList(
    params: {
      page?: number;
      size?: number;
      orderBy?: string;
      direction?: 'ASC' | 'DESC';
      [key: string]: any;
    } = {}
  ): Observable<Paginate<OdvDocuments>> {
    Array.from(this.getUrlParameters().keys()).forEach((key) => this.removeUrlParameter(key));
    if (params.page) this.setUrlParameter('page', params.page.toString());
    if (params.size) this.setUrlParameter('size', params.size.toString());
    if (params.orderBy) this.setUrlParameter('orderBy', params.orderBy);
    if (params.direction) this.setUrlParameter('direction', params.direction);

    Object.keys(params).forEach((key) => {
      if (
        !['page', 'size', 'orderBy', 'direction'].includes(key) &&
        params[key] !== undefined &&
        params[key] !== null
      ) {
        this.setUrlParameter(key, params[key]);
      }
    });
    return this.paginate(OdvDocuments, params.page || 1, params.size || PER_PAGE);
  }

  /**
   * Clears one uploaded file, leaving the sales order document requirement in place.
   * Removing the requirement itself is SalesOrderDocumentsService.deleteSalesOrderDocument().
   */
  deleteOdvDocument(id: string): Observable<boolean> {
    this.logger.debug(LOG_PREFIX + `delete odv document ${id}`);
    this.setUrlParameter('id', id);
    return this.remove(id);
  }

  getBySalesOrderDocumentId(salesOrderDocumentId: string): Observable<Paginate<OdvDocuments>> {
    return this.getList({ salesOrderDocumentId });
  }
}
